import React, { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  Key,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Unlock,
  Zap,
  Shield,
  X,
  Check,
  AlertCircle,
  Fingerprint,
  Scan,
  RefreshCw
} from "lucide-react";
import notify from "../../../utility/NotificationSystem";
import { authActions } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import WrapUpP2P from "../p2p/layouts/WrapUpP2p";

const ChangePassword = () => {
  // Mock Redux - replace with actual selectors
  const userData = { username: "investor_alex" };

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [securityObj, setSecurityObj] = useState({});
  const [passwordUpdate, setPasswordUpdate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [notification, setNotification] = useState(null);
  const [securityLevel, setSecurityLevel] = useState(2);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const dispatch = useDispatch()

  // Password strength calculation
  const calculatePasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength += 25;
    if (pass.match(/[a-z]/)) strength += 15;
    if (pass.match(/[A-Z]/)) strength += 15;
    if (pass.match(/[0-9]/)) strength += 15;
    if (pass.match(/[^a-zA-Z0-9]/)) strength += 30;
    return Math.min(strength, 100);
  };

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  const getStrengthColor = () => {
    if (passwordStrength < 30) return "from-red-500 to-red-600";
    if (passwordStrength < 60) return "from-yellow-500 to-orange-500";
    if (passwordStrength < 80) return "from-blue-500 to-cyan-500";
    return "from-green-500 to-emerald-600";
  };

  const getStrengthText = () => {
    if (passwordStrength < 30) return "Weak";
    if (passwordStrength < 60) return "Fair";
    if (passwordStrength < 80) return "Good";
    return "Strong";
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const doPasswordChange = async (e) => {
    if (passwordUpdate) {
      return
    }
    e.preventDefault()

    if (password == "") {
      notify("Please input new password", 0)
      return false
    }

    if (password !== password2) {
      notify("Password mismatch. Enter valid passwords", 0)
      return false
    }

    const data = { password, ...(securityObj ?? {}) }

    setPasswordUpdate(true)

    dispatch(
      authActions.onLogin("account/change_password", data, (response) => {
        if (response.code == 1) {
          setSecurityObj({})
          setPassword("")
          setPassword2("")
        }
        notify(response.msg, response.code)
        setPasswordUpdate(false)
      }),
    )
  }


  const passwordRequirements = [
    { text: "At least 8 characters", met: password.length >= 8 },
    { text: "Contains lowercase letter", met: /[a-z]/.test(password) },
    { text: "Contains uppercase letter", met: /[A-Z]/.test(password) },
    { text: "Contains number", met: /[0-9]/.test(password) },
    { text: "Contains special character", met: /[^a-zA-Z0-9]/.test(password) }
  ];

  return (<WrapUpP2P allowAutoPadding={false}>
    <div className="min-h-screen  text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(250, 204, 21) 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center">
              <Lock className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 bg-clip-text text-transparent">
                Security Center
              </h1>
              <p className="text-gray-400 font-mono">Advanced password management system</p>
            </div>
          </div>

        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Password Form */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl border border-yellow-500/20 overflow-hidden backdrop-blur-xl">
              <div className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 p-6 border-b border-yellow-500/20">
                <div className="flex items-center space-x-3">
                  <Key className="w-6 h-6 text-yellow-400" />
                  <h2 className="text-xl font-bold text-white">Password Update Protocol</h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mt-2">
                  Enhance your account security with a new password
                </p>
              </div>

              <div className="p-8">
                <div className="space-y-8">
                  {/* New Password */}
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-yellow-400 font-mono uppercase tracking-wider">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-4 bg-black/50 border border-gray-700 rounded-xl focus:border-yellow-500 focus:outline-none text-white font-mono placeholder-gray-400 pr-12"
                        placeholder="Enter new password..."
                      />
                      <button
                        type="button"
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-yellow-400 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {password && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400 font-mono">Password Strength</span>
                          <span className={`text-sm font-bold bg-gradient-to-r ${getStrengthColor()} bg-clip-text text-transparent`}>
                            {getStrengthText()} ({passwordStrength}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r ${getStrengthColor()} transition-all duration-500 ease-out`}
                            style={{ width: `${passwordStrength}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-yellow-400 font-mono uppercase tracking-wider">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                        className="w-full px-4 py-4 bg-black/50 border border-gray-700 rounded-xl focus:border-yellow-500 focus:outline-none text-white font-mono placeholder-gray-400 pr-12"
                        placeholder="Confirm new password..."
                      />
                      <button
                        type="button"
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-yellow-400 transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>

                    {/* Password Match Indicator */}
                    {password2 && (
                      <div className="flex items-center space-x-2">
                        {password === password2 ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                            <span className="text-green-400 text-sm font-mono">Passwords match</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 text-red-400" />
                            <span className="text-red-400 text-sm font-mono">Passwords don't match</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={doPasswordChange}
                      disabled={passwordUpdate || passwordStrength < 60 || password !== password2}
                      className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold rounded-xl hover:from-yellow-300 hover:to-amber-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {passwordUpdate ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          <span>UPDATING...</span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5" />
                          <span>UPDATE PASSWORD</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setPassword("");
                        setPassword2("");
                        setSecurityObj({});
                      }}
                      className="px-6 py-4 bg-gray-800 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-all duration-300"
                    >
                      RESET
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Password Requirements */}
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl border border-yellow-500/20 p-6 backdrop-blur-xl">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <Unlock className="w-5 h-5 text-yellow-400 mr-2" />
                Password Requirements
              </h3>
              <div className="space-y-3">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${req.met ? 'bg-green-500/20 text-green-400' : 'bg-gray-700/50 text-gray-500'
                      }`}>
                      {req.met ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    </div>
                    <span className={`text-sm font-mono ${req.met ? 'text-green-400' : 'text-gray-400'}`}>
                      {req.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Tips */}
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl border border-yellow-500/20 p-6 backdrop-blur-xl">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <Shield className="w-5 h-5 text-yellow-400 mr-2" />
                Security Tips
              </h3>
              <div className="space-y-4 text-sm text-gray-300">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-yellow-400 mt-2"></div>
                  <p>Use a unique password for your account</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-yellow-400 mt-2"></div>
                  <p>Never share your password with anyone</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-yellow-400 mt-2"></div>
                  <p>Update your password regularly</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className="fixed top-6 right-6 z-50 max-w-sm">
          <div className="bg-black/90 backdrop-blur-xl rounded-2xl border border-yellow-500/30 p-4 shadow-2xl">
            <div className="flex items-center space-x-3">
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${notification.type === 'success'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
                }`}>
                {notification.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
              </div>
              <p className="text-white font-mono text-sm flex-1">{notification.message}</p>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm border-t border-yellow-500/20 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between text-xs font-mono text-gray-400">
          <span>SECURITY_PROTOCOL: <span className="text-green-400">ACTIVE</span></span>
          <span>ENCRYPTION: <span className="text-green-400">AES-256</span></span>
          <span>SESSION: <span className="text-yellow-400">SECURE</span></span>
        </div>
      </div>
    </div>
  </WrapUpP2P>
  );
};

export default ChangePassword;